// sse-client.ts
type Payload = any;
type EventHandler = (data: Payload) => void;

export class SSEClient {
  private url: string;
  private eventSource: EventSource;
  private id: string | null = null;
  private handlers: Record<string, EventHandler> = {};

  constructor(url: string) {
    this.url = url;
    this.eventSource = new EventSource(this.url);

    // 1) "connect" event to grab assigned clientId
    this.eventSource.addEventListener('connect', (e: MessageEvent) => {
      let parsed: any;
      try {
        parsed = JSON.parse(e.data);
      } catch {
        parsed = e.data;
      }
      this.id = parsed.clientId;
      this.callHandler('connect', parsed);
    });

    // 2) Generic "message" (if server emits with no explicit event name)
    this.eventSource.addEventListener('message', (e: MessageEvent) => {
      let parsed: any;
      try {
        parsed = JSON.parse(e.data);
      } catch {
        parsed = e.data;
      }
      this.callHandler('message', parsed);
    });
  }

  /**
   * Listen for a custom event from the server (including queries).
   * @param eventName e.g. 'welcome', 'count-indexeddb', etc.
   */
  public on(eventName: string, callback: EventHandler): void {
    this.handlers[eventName] = callback;

    // If it's not "message" or "connect", register it on EventSource
    if (eventName !== 'message' && eventName !== 'connect') {
      this.eventSource.addEventListener(eventName, (e: MessageEvent) => {
        let parsed: any;
        try {
          parsed = JSON.parse(e.data);
        } catch {
          parsed = e.data;
        }
        callback(parsed);
      });
    }
  }

  /**
   * Send a message (or a query-response) back to server.
   * Internally does POST to /socket/:id with { event, data }.
   */
  public emit(eventName: string, data: Payload): void {
    if (!this.id) {
      console.warn('[SSEClient] Call emit() only after "connect" received.');
    }
    fetch(`${this.url}/${this.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: eventName, data }),
    }).catch((err) => {
      console.error('[SSEClient] Error emitting event:', err);
    });
  }

  /**
   * Send a query response to the server using the new endpoint.
   */
  public emitQueryResponse(requestId: string, data: Payload): void {
    fetch(`${this.url}/respond-query/${requestId}`, {
      method: 'POST',
  headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data }),
    }).catch((err) => {
      console.error('[SSEClient] Error emitting query response:', err);
    });
  }

  /** @internal */
  private callHandler(eventName: string, data: Payload) {
    const h = this.handlers[eventName];
    if (h) h(data);
  }
}
